import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  useTheme,
  alpha,
  Button,
  Autocomplete,
  TextField,
  Avatar,
  CircularProgress,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Tooltip,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import { useTheme as useCustomTheme } from '../../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { ArrowBack, PersonAdd, PersonRemove, AdminPanelSettings, Shield } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

// Import API
import { adminApi } from '../../services/api';
import { userApi } from '../../services/userApi';

// Import images
import ShieldImage from '../../assets/GlassElement.png';

// User interface for the dropdown
interface User {
  _id: string;
  username: string;
  email: string;
  profilePicture?: string;
  createdAt?: string;
}

interface AdminUser extends User {
  isCurrentUser?: boolean;
}

const ManageAdminsPage: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const { mode } = useCustomTheme();
  const navigate = useNavigate();
  
  const isDark = mode === 'dark';
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';

  // State for admin management
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openRemoveDialog, setOpenRemoveDialog] = useState(false);
  const [userToRemove, setUserToRemove] = useState<AdminUser | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const discordFont = {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
    fontWeight: 800,
    letterSpacing: '-0.02em',
  };

  const glassEffect = {
    background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(20px)',
    border: isDark 
      ? '1px solid rgba(196, 182, 111, 0.2)' 
      : '1px solid rgba(5, 24, 20, 0.1)',
    boxShadow: isDark 
      ? '0 20px 60px rgba(0,0,0,0.7)' 
      : '0 20px 60px rgba(5, 24, 20, 0.1)',
  };

  const dropdownGlassEffect = {
    background: isDark ? 'rgba(5, 24, 20, 0.95)' : 'rgba(255, 255, 255, 0.98)',
    backdropFilter: 'blur(20px)',
    border: isDark 
      ? '1px solid rgba(196, 182, 111, 0.3)' 
      : '1px solid rgba(5, 24, 20, 0.15)',
    boxShadow: isDark 
      ? '0 20px 60px rgba(0,0,0,0.8)' 
      : '0 20px 60px rgba(5, 24, 20, 0.15)',
  };

  // Fetch all users and admin users
  useEffect(() => {
    if (user) {
      fetchAdminUsers();
      fetchAllUsers();
    }
  }, [user]);

  const fetchAdminUsers = async () => {
    try {
      setLoadingAdmins(true);
      const response = await adminApi.getAdminUsers();
      if (response.success) {
        const adminsWithCurrentUser = response.admins.map((admin: User) => ({
          ...admin,
          isCurrentUser: admin._id === user?._id,
        }));
        setAdminUsers(adminsWithCurrentUser);
      }
    } catch (error) {
      console.error('Error fetching admin users:', error);
      toast.error('Failed to load admin users');
    } finally {
      setLoadingAdmins(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await userApi.getAllUsers();
      if (response.success) {
        setAllUsers(response.users || []);
      }
    } catch (error) {
      console.error('Error fetching all users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleAddAdmin = async () => {
    if (!selectedUser) {
      toast.error('Please select a user');
      return;
    }

    try {
      setLoading(true);
      const response = await adminApi.addAdmin(selectedUser._id);
      
      if (response.success) {
        toast.success(`${selectedUser.username} added as admin successfully`);
        setSelectedUser(null);
        setOpenAddDialog(false);
        fetchAdminUsers(); // Refresh the list
        fetchAllUsers(); // Refresh all users list
        
        // Show success snackbar
        setSnackbar({
          open: true,
          message: `${selectedUser.username} is now an admin`,
          severity: 'success',
        });
      }
    } catch (error: any) {
      console.error('Error adding admin:', error);
      const errorMessage = error.response?.data?.error || 'Failed to add admin';
      toast.error(errorMessage);
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAdmin = async (adminUser: AdminUser) => {
    if (adminUser.isCurrentUser) {
      toast.error('You cannot remove yourself as admin');
      return;
    }

    try {
      setLoading(true);
      const response = await adminApi.removeAdmin(adminUser._id);
      
      if (response.success) {
        toast.success(`${adminUser.username} removed from admins`);
        setUserToRemove(null);
        setOpenRemoveDialog(false);
        fetchAdminUsers(); // Refresh the list
        fetchAllUsers(); // Refresh all users list
        
        setSnackbar({
          open: true,
          message: `${adminUser.username} removed from admins`,
          severity: 'success',
        });
      }
    } catch (error: any) {
      console.error('Error removing admin:', error);
      const errorMessage = error.response?.data?.error || 'Failed to remove admin';
      toast.error(errorMessage);
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const openRemoveConfirmDialog = (adminUser: AdminUser) => {
    setUserToRemove(adminUser);
    setOpenRemoveDialog(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (!user) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: isDark ? '#000000' : '#FAFAFA', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <Container maxWidth="sm">
          <Paper sx={{ 
            p: 6, 
            borderRadius: '32px', 
            textAlign: 'center', 
            ...glassEffect 
          }}>
            <Shield sx={{ fontSize: 80, color: goldColor, mb: 3 }} />
            <Typography 
              variant="h4" 
              sx={{ 
                ...discordFont, 
                color: isDark ? '#FFFFFF' : deepGreen, 
                mb: 2 
              }}
            >
              Admin Access Required
            </Typography>
            <Typography sx={{ 
              color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)', 
              mb: 4 
            }}>
              You need to be logged in as an admin to access this page.
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => navigate('/login')} 
              sx={{ 
                background: isDark ? 'rgba(196, 182, 111, 0.9)' : '#051814', 
                color: isDark ? '#051814' : '#FFFFFF', 
                borderRadius: '16px', 
                px: 4, 
                py: 1.5, 
                fontWeight: 700, 
                '&:hover': { 
                  background: isDark ? 'rgba(196, 182, 111, 1)' : '#0F3B2E' 
                } 
              }}
            >
              Go to Login
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: isDark ? '#000000' : '#FAFAFA',
      overflowX: 'hidden',
      // Hide scrollbar for mobile
      '& ::-webkit-scrollbar': {
        display: 'none',
      },
      '&': {
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      },
    }}>
      <Container maxWidth={false} sx={{ 
        py: 4,
        px: { xs: 2, sm: 3, md: 4, lg: 6, xl: 8 },
        maxWidth: '100% !important',
      }}>
        {/* Back Button */}
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/admin')} 
          sx={{ 
            mb: 3, 
            color: goldColor, 
            borderRadius: '12px',
            px: 3,
            '&:hover': { 
              background: isDark ? 'rgba(196, 182, 111, 0.1)' : 'rgba(196, 182, 111, 0.05)' 
            } 
          }}
        >
          Back
        </Button>

        {/* Header with Shield Image */}
        <Paper sx={{ 
          p: 0, 
          mb: 6, 
          borderRadius: '32px', 
          overflow: 'hidden', 
          ...glassEffect, 
          display: 'flex', 
          flexDirection: { xs: 'column-reverse', md: 'row' }, 
          alignItems: 'center',
          textAlign: { xs: 'center', md: 'left' },
        }}>
          <Box sx={{ 
            p: { xs: 3, md: 6 }, 
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'center', md: 'flex-start' },
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 2,
              justifyContent: { xs: 'center', md: 'flex-start' },
            }}>
              <AdminPanelSettings sx={{ fontSize: { xs: 32, md: 40 }, color: goldColor, mr: 2 }} />
              <Typography variant="h3" sx={{ 
                ...discordFont, 
                background: isDark 
                  ? 'linear-gradient(135deg, #C4B66F 0%, #FFFFFF 100%)' 
                  : `linear-gradient(135deg, ${deepGreen} 0%, ${deepGreen} 100%)`, 
                backgroundClip: 'text', 
                WebkitBackgroundClip: 'text', 
                color: 'transparent', 
                fontSize: { xs: '1.75rem', md: '3rem' },
                textAlign: { xs: 'center', md: 'left' },
              }}>
                Manage Administrators
              </Typography>
            </Box>
            <Typography variant="h6" sx={{ 
              color: isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(5, 24, 20, 0.8)', 
              fontWeight: 400, 
              maxWidth: 600,
              textAlign: { xs: 'center', md: 'left' },
              fontSize: { xs: '1rem', md: '1.25rem' },
            }}>
              Add or remove administrator privileges for WFHCafe users. 
              Administrators have full access to the admin dashboard.
            </Typography>
          </Box>
          <Box sx={{ 
            width: { xs: '100%', md: '40%' }, 
            height: { xs: 180, md: '100%' }, 
            minHeight: { md: 300 }, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            p: { xs: 2, md: 4 } 
          }}>
            <img 
              src={ShieldImage} 
              alt="Admin Shield" 
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'contain', 
                maxHeight: 250 
              }} 
            />
          </Box>
        </Paper>

        {/* Main Content - Full width on desktop */}
        <Grid container spacing={4} sx={{ maxWidth: '100%' }}>
          {/* Left Column - Current Admins */}
          <Grid size={{ xs: 12, md: 6 }}
          sx={{ width: '100%' }}>
            <Paper sx={{ 
              p: 4, 
              borderRadius: '24px', 
              height: '100%',
              ...glassEffect,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <Shield sx={{ 
                  fontSize: 32, 
                  color: goldColor, 
                  mr: 2 
                }} />
                <Typography variant="h5" sx={{ 
                  ...discordFont, 
                  color: isDark ? '#FFFFFF' : deepGreen,
                }}>
                  Current Administrators
                  <Chip 
                    label={loadingAdmins ? '...' : adminUsers.length.toString()} 
                    size="small" 
                    sx={{ 
                      ml: 2,
                      ...glassEffect,
                      color: goldColor,
                      fontWeight: 600,
                    }} 
                  />
                </Typography>
              </Box>
              
              {loadingAdmins ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress sx={{ color: goldColor }} />
                </Box>
              ) : adminUsers.length === 0 ? (
                <Card sx={{ 
                  ...glassEffect,
                  borderRadius: '16px',
                  textAlign: 'center',
                  py: 6,
                }}>
                  <CardContent>
                    <AdminPanelSettings sx={{ 
                      fontSize: 60, 
                      color: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(5, 24, 20, 0.2)',
                      mb: 2,
                    }} />
                    <Typography sx={{ 
                      color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(5, 24, 20, 0.6)', 
                      fontStyle: 'italic',
                    }}>
                      No administrators found. Add your first admin using the form.
                    </Typography>
                  </CardContent>
                </Card>
              ) : (
                <Box sx={{ 
                  ...glassEffect,
                  borderRadius: '16px',
                  overflow: 'hidden',
                  maxHeight: 500,
                  overflowY: 'auto',
                  // Hide scrollbar for mobile
                  '& ::-webkit-scrollbar': {
                    display: 'none',
                  },
                  '&': {
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                  },
                }}>
                  <List sx={{ 
                    bgcolor: 'transparent',
                  }}>
                    {adminUsers.map((adminUser, index) => (
                      <React.Fragment key={adminUser._id}>
                        <ListItem
                          secondaryAction={
                            !adminUser.isCurrentUser ? (
                              <Tooltip title="Remove Admin">
                                <IconButton 
                                  edge="end" 
                                  onClick={() => openRemoveConfirmDialog(adminUser)}
                                  sx={{ 
                                    color: isDark ? '#ff7777' : '#ff4444',
                                    ...glassEffect,
                                    p: 1,
                                    '&:hover': {
                                      background: isDark ? 'rgba(255, 119, 119, 0.1)' : 'rgba(255, 68, 68, 0.1)',
                                    }
                                  }}
                                >
                                  <PersonRemove />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Chip 
                                label="You" 
                                size="small" 
                                sx={{ 
                                  ...glassEffect,
                                  color: goldColor,
                                  fontWeight: 600,
                                }} 
                              />
                            )
                          }
                        >
                          <ListItemAvatar>
                            <Avatar
                              src={adminUser.profilePicture}
                              sx={{ 
                                width: 48,
                                height: 48,
                                ...glassEffect,
                                color: goldColor,
                                fontSize: '1.2rem',
                                border: `2px solid ${isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(196, 182, 111, 0.2)'}`,
                              }}
                            >
                              {adminUser.username.charAt(0).toUpperCase()}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                                <Typography sx={{ 
                                  fontWeight: 700,
                                  color: isDark ? '#FFFFFF' : deepGreen,
                                  mr: 1,
                                }}>
                                  {adminUser.username}
                                </Typography>
                                {adminUser.isCurrentUser && (
                                  <Chip 
                                    label="Current User" 
                                    size="small" 
                                    sx={{ 
                                      ...glassEffect,
                                      color: goldColor,
                                      fontSize: '0.7rem',
                                      height: 20,
                                    }} 
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Typography sx={{ 
                                color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(5, 24, 20, 0.6)',
                                fontSize: '0.875rem',
                                mt: 0.5,
                              }}>
                                {adminUser.email}
                              </Typography>
                            }
                          />
                        </ListItem>
                        {index < adminUsers.length - 1 && (
                          <Divider component="li" sx={{ 
                            borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(5, 24, 20, 0.1)' 
                          }} />
                        )}
                      </React.Fragment>
                    ))}
                  </List>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Right Column - Add New Admin */}
          <Grid size={{ xs: 12, md: 6 }}
    sx={{ width: '100%' }}>
            <Paper sx={{ 
              p: 4, 
              borderRadius: '24px', 
              height: '100%',
              ...glassEffect,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <PersonAdd sx={{ 
                  fontSize: 32, 
                  color: goldColor, 
                  mr: 2 
                }} />
                <Typography variant="h5" sx={{ 
                  ...discordFont, 
                  color: isDark ? '#FFFFFF' : deepGreen,
                }}>
                  Add New Administrator
                </Typography>
              </Box>
              
              <Box sx={{ mb: 4 }}>
                <Typography sx={{ 
                  color: isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(5, 24, 20, 0.8)', 
                  mb: 2,
                  fontWeight: 500,
                }}>
                  Search for users by username or email address
                </Typography>
                
                {loadingUsers ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress sx={{ color: goldColor }} />
                  </Box>
                ) : (
                  <>
                    <Autocomplete
                      sx={{ 
                        mb: 3,
                        '& .MuiAutocomplete-popper': {
                          '& .MuiPaper-root': {
                            ...dropdownGlassEffect,
                            borderRadius: '12px',
                            marginTop: '8px',
                          }
                        },
                        '& .MuiAutocomplete-inputRoot': {
                          color: isDark ? '#FFFFFF' : deepGreen,
                          ...glassEffect,
                          borderRadius: '12px',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: isDark ? 'rgba(196, 182, 111, 0.5)' : 'rgba(5, 24, 20, 0.3)',
                          },
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(5, 24, 20, 0.2)',
                        },
                      }}
                      options={allUsers.filter(user => 
                        !adminUsers.some(admin => admin._id === user._id)
                      )}
                      getOptionLabel={(option) => option.username || option.email}
                      renderOption={(props, option) => (
                        <Box 
                          component="li" 
                          {...props} 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            py: 1.5,
                            px: 2,
                            '&:hover': {
                              background: isDark ? 'rgba(196, 182, 111, 0.1)' : 'rgba(5, 24, 20, 0.05)',
                            }
                          }}
                        >
                          <Avatar 
                            src={option.profilePicture} 
                            sx={{ 
                              width: 36, 
                              height: 36, 
                              mr: 2,
                              ...glassEffect,
                              color: goldColor,
                              border: `1px solid ${isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(196, 182, 111, 0.2)'}`,
                            }}
                          >
                            {option.username?.charAt(0)?.toUpperCase() || option.email?.charAt(0)?.toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography sx={{ 
                              fontWeight: 600,
                              color: isDark ? '#FFFFFF' : deepGreen,
                            }}>
                              {option.username || 'No username'}
                            </Typography>
                            <Typography sx={{ 
                              fontSize: '0.875rem',
                              color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(5, 24, 20, 0.6)',
                            }}>
                              {option.email}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Search users by username or email..."
                          variant="outlined"
                          fullWidth
                          InputLabelProps={{
                            style: { 
                              color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)',
                              fontWeight: 500,
                            },
                          }}
                          InputProps={{
                            ...params.InputProps,
                            sx: {
                              color: isDark ? '#FFFFFF' : deepGreen,
                            }
                          }}
                        />
                      )}
                      value={selectedUser}
                      onChange={(event, newValue) => setSelectedUser(newValue)}
                      isOptionEqualToValue={(option, value) => option._id === value?._id}
                      noOptionsText={
                        <Typography sx={{ 
                          color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(5, 24, 20, 0.6)',
                          py: 2,
                          px: 2,
                          fontStyle: 'italic',
                        }}>
                          No users found
                        </Typography>
                      }
                    />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        startIcon={<PersonAdd />}
                        onClick={() => setOpenAddDialog(true)}
                        disabled={!selectedUser}
                        sx={{ 
                          ...glassEffect,
                          color: goldColor,
                          fontWeight: 700,
                          borderRadius: '12px',
                          px: 4,
                          py: 1.5,
                          minWidth: 200,
                          border: `1px solid ${isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(196, 182, 111, 0.2)'}`,
                          '&:hover': {
                            background: isDark ? 'rgba(196, 182, 111, 0.1)' : 'rgba(196, 182, 111, 0.05)',
                            borderColor: isDark ? 'rgba(196, 182, 111, 0.5)' : 'rgba(196, 182, 111, 0.3)',
                          },
                          '&:disabled': {
                            background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(5, 24, 20, 0.05)',
                            color: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(5, 24, 20, 0.3)',
                            borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(5, 24, 20, 0.1)',
                          },
                        }}
                      >
                        Add as Admin
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
              
              <Box sx={{ 
                mt: 6,
                p: 3,
                borderRadius: '16px',
                ...glassEffect,
                border: `1px solid ${isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(196, 182, 111, 0.2)'}`,
              }}>
                <Typography sx={{ 
                  color: goldColor,
                  fontWeight: 600,
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}>
                  <Shield sx={{ fontSize: 20 }} />
                  Administrator Privileges
                </Typography>
                <Typography sx={{ 
                  color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)',
                  fontSize: '0.875rem',
                  mb: 2,
                }}>
                  Administrators have full access to:
                </Typography>
                <Box component="ul" sx={{ 
                  pl: 2, 
                  mt: 1,
                  '& li': {
                    color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)',
                    fontSize: '0.875rem',
                    mb: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    '&::before': {
                      content: '"✓"',
                      color: goldColor,
                      fontWeight: 'bold',
                    }
                  }
                }}>
                  <li>Feedback management</li>
                  <li>Reports and analytics</li>
                  <li>User management</li>
                  <li>System configuration</li>
                  <li>All admin-only features</li>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Add Admin Confirmation Dialog */}
      <Dialog 
        open={openAddDialog} 
        onClose={() => !loading && setOpenAddDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: '24px',
            ...glassEffect,
            minWidth: { xs: '90%', sm: 400 },
            maxWidth: 500,
            border: `1px solid ${isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(5, 24, 20, 0.2)'}`,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.25rem', p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', color: goldColor }}>
            <PersonAdd sx={{ mr: 1.5 }} />
            Confirm Add Administrator
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 0 }}>
          {selectedUser && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 3,
              p: 2.5,
              borderRadius: '16px',
              ...glassEffect,
              border: `1px solid ${isDark ? 'rgba(196, 182, 111, 0.2)' : 'rgba(5, 24, 20, 0.1)'}`,
            }}>
              <Avatar
                src={selectedUser.profilePicture}
                sx={{ 
                  width: 56, 
                  height: 56, 
                  mr: 3,
                  ...glassEffect,
                  color: goldColor,
                  fontSize: '1.5rem',
                  border: `2px solid ${isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(196, 182, 111, 0.2)'}`,
                }}
              >
                {selectedUser.username?.charAt(0)?.toUpperCase() || selectedUser.email?.charAt(0)?.toUpperCase()}
              </Avatar>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: isDark ? '#FFFFFF' : deepGreen }}>
                  {selectedUser.username || 'No username'}
                </Typography>
                <Typography sx={{ 
                  color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)',
                  fontSize: '0.9rem',
                }}>
                  {selectedUser.email}
                </Typography>
              </Box>
            </Box>
          )}
          <Typography sx={{ 
            color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(5, 24, 20, 0.9)',
            lineHeight: 1.6,
          }}>
            Are you sure you want to grant administrator privileges to this user? 
            They will have full access to the admin dashboard and all its features.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={() => setOpenAddDialog(false)}
            disabled={loading}
            sx={{ 
              ...glassEffect,
              color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)',
              fontWeight: 500,
              borderRadius: '12px',
              px: 3,
              border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(5, 24, 20, 0.1)'}`,
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddAdmin}
            disabled={loading}
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} sx={{ color: deepGreen }} /> : null}
            sx={{ 
              ...glassEffect,
              color: goldColor,
              fontWeight: 700,
              borderRadius: '12px',
              px: 3,
              border: `1px solid ${isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(196, 182, 111, 0.2)'}`,
              '&:hover': {
                background: isDark ? 'rgba(196, 182, 111, 0.1)' : 'rgba(196, 182, 111, 0.05)',
                borderColor: isDark ? 'rgba(196, 182, 111, 0.5)' : 'rgba(196, 182, 111, 0.3)',
              },
            }}
          >
            {loading ? 'Adding...' : 'Confirm Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Admin Confirmation Dialog */}
      <Dialog 
        open={openRemoveDialog} 
        onClose={() => !loading && setOpenRemoveDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: '24px',
            ...glassEffect,
            minWidth: { xs: '90%', sm: 400 },
            maxWidth: 500,
            border: `1px solid ${isDark ? 'rgba(255, 68, 68, 0.3)' : 'rgba(255, 68, 68, 0.2)'}`,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.25rem', p: 3, color: isDark ? '#ff7777' : '#ff4444' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonRemove sx={{ mr: 1.5 }} />
            Remove Administrator
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 0 }}>
          {userToRemove && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 3,
              p: 2.5,
              borderRadius: '16px',
              ...glassEffect,
              border: `1px solid ${isDark ? 'rgba(255, 68, 68, 0.2)' : 'rgba(255, 68, 68, 0.1)'}`,
            }}>
              <Avatar
                src={userToRemove.profilePicture}
                sx={{ 
                  width: 56, 
                  height: 56, 
                  mr: 3,
                  ...glassEffect,
                  border: `2px solid ${isDark ? 'rgba(255, 68, 68, 0.3)' : 'rgba(255, 68, 68, 0.2)'}`,
                }}
              >
                {userToRemove.username?.charAt(0)?.toUpperCase() || userToRemove.email?.charAt(0)?.toUpperCase()}
              </Avatar>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: isDark ? '#FFFFFF' : deepGreen }}>
                  {userToRemove.username || 'No username'}
                </Typography>
                <Typography sx={{ 
                  color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)',
                  fontSize: '0.9rem',
                }}>
                  {userToRemove.email}
                </Typography>
              </Box>
            </Box>
          )}
          <Typography sx={{ 
            color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(5, 24, 20, 0.9)',
            lineHeight: 1.6,
          }}>
            Are you sure you want to remove administrator privileges from this user? 
            They will lose access to all admin features and the admin dashboard.
          </Typography>
          <Box sx={{ 
            mt: 2,
            p: 2,
            borderRadius: '12px',
            ...glassEffect,
            border: `1px solid ${isDark ? 'rgba(255, 193, 7, 0.3)' : 'rgba(255, 193, 7, 0.2)'}`,
          }}>
            <Typography sx={{ 
              color: isDark ? '#ffc107' : '#856404',
              fontSize: '0.875rem',
              fontWeight: 500,
            }}>
              ⚠️ This action cannot be undone. The user will need to be added again if needed.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={() => setOpenRemoveDialog(false)}
            disabled={loading}
            sx={{ 
              ...glassEffect,
              color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)',
              fontWeight: 500,
              borderRadius: '12px',
              px: 3,
              border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(5, 24, 20, 0.1)'}`,
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => userToRemove && handleRemoveAdmin(userToRemove)}
            disabled={loading}
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} sx={{ color: '#FFFFFF' }} /> : null}
            sx={{ 
              ...glassEffect,
              background: isDark ? 'rgba(255, 68, 68, 0.2)' : 'rgba(255, 68, 68, 0.1)',
              color: isDark ? '#ff7777' : '#ff4444',
              fontWeight: 700,
              borderRadius: '12px',
              px: 3,
              border: `1px solid ${isDark ? 'rgba(255, 68, 68, 0.3)' : 'rgba(255, 68, 68, 0.2)'}`,
              '&:hover': {
                background: isDark ? 'rgba(255, 68, 68, 0.3)' : 'rgba(255, 68, 68, 0.2)',
                borderColor: isDark ? 'rgba(255, 68, 68, 0.5)' : 'rgba(255, 68, 68, 0.3)',
              },
            }}
          >
            {loading ? 'Removing...' : 'Confirm Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            borderRadius: '16px',
            ...glassEffect,
            border: `1px solid ${snackbar.severity === 'success' ? 
              (isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(196, 182, 111, 0.2)') : 
              (isDark ? 'rgba(255, 68, 68, 0.3)' : 'rgba(255, 68, 68, 0.2)')}`,
            color: snackbar.severity === 'success' ? goldColor : (isDark ? '#ff7777' : '#ff4444'),
            fontWeight: 500,
          }}
          icon={snackbar.severity === 'success' ? <PersonAdd /> : <PersonRemove />}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageAdminsPage;